// ============================================================
// MIDAS V3 — machine_card chat viewer (ME-MACHINEID-RENDER SP-2)
// ============================================================
// The chat-host (a) renderer for a `machine_card` artifact under the LOCKED
// ME-CONTRACT nested shape. A THIN wrapper that parses `tab.artifact.data` into
// the SP-1a model and composes the SP-1a honesty atoms — it does NOT re-derive
// freshness / coverage / verdict. Binding: §7A flag #1 — freshness comes from the
// bot-emitted `data.freshness.state` via `resolveFreshness`; nothing here
// recomputes it from `age_seconds`/thresholds (that would force a legitimately-
// STALE 20-day backfill to DARK and break the acceptance).
//
// Iterates `data.tabs[]` GENERICALLY (tab-list-agnostic, GAP-9). The hardcoded
// six-tab label map + `arm` deep-link constant only supply display labels + tab
// order — a stray/unknown slug still renders. Emit gate is OFF; this renders live
// only after the joint flip. Composes: identity header, card-level freshness
// banner, promoted exception banner (GAP-8), the FUNCTIONAL 5-pill period-window
// selector (SP-1b — shown only when the card carries windowed figures; hidden for
// all-snapshot cards), six-tab overview roll-up (worst verdict), per-section body,
// provenance footer.
import React, { useMemo, useState } from "react";

import type {
  MachineCardArtifactData,
  WindowSelection,
} from "../../../../../shared/components/machineEvidence";
import {
  ExceptionBanner,
  FreshnessBanner,
  ProvenanceFooter,
  buildProvenanceFooter,
  cardHasWindowedFigures,
  deriveExceptionBanner,
  initialWindowSelection,
  projectFigureToSelection,
  resolveFreshness,
  resolveIdentityTitle,
  resolveTurnWindow,
} from "../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../shared/themes/redesignTokens";
import { ArtifactTab } from "../../../midasPageModels";
import { isOverviewCard, orderedSectionTabs } from "./machineCardConstants";
import {
  OverviewRollup,
  PeriodPillRow,
  SectionBody,
  useTabLabel,
  windowSelectionLabel,
} from "./machineCardSections";

// Re-export the deep-link constant so the SP-3 chat affordance + a future SP-4
// machine_evidence viewer can target the merged Picker+Jaws tab by name.
export { MACHINE_CARD_ARM_TAB_SLUG } from "./machineCardConstants";

export interface MidasArtifactMachineCardV3Props {
  tab: ArtifactTab;
  /** Deep-link target tab slug (e.g. `arm`), honored by an opener. Falls back to
   *  a client render hint on the artifact data, then the first section. */
  initialTabSlug?: string;
}

const Empty: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: dt.font,
      fontSize: 13,
      color: dt.textMuted,
    }}
  >
    {message}
  </div>
);

const MidasArtifactMachineCardV3: React.FC<MidasArtifactMachineCardV3Props> = ({
  tab,
  initialTabSlug,
}) => {
  const { translate } = useTranslation("machineEvidence");
  const translateTabLabel = useTabLabel();
  const data = tab.artifact.data as unknown as MachineCardArtifactData | undefined;
  // Client render hint (not part of ME-CONTRACT): an opener may stash the deep-
  // link target tab on the artifact data so the card opens on that section.
  const hintSlug = (tab.artifact.data as { _deepLinkTabSlug?: string } | undefined)?._deepLinkTabSlug;

  const ordered = useMemo(() => (data ? orderedSectionTabs(data) : []), [data]);

  const [activeSlug, setActiveSlug] = useState<string>(() => {
    const preferred = initialTabSlug || hintSlug;
    if (preferred && (data?.tabs ?? []).some((t) => t.part_slug === preferred)) return preferred;
    return (data?.tabs ?? [])[0]?.part_slug ?? "";
  });

  // SP-1b — card-level window selector; switching is purely client-side over the
  // delivered `windows[]` (no refetch).
  //
  // ME-CARDS-R1 A2: the initial selection is the card's TURN-resolved window —
  // the figure's own primary read, i.e. exactly what the verdict headline and
  // the provenance footer were computed on. The previous hardcoded
  // `DEFAULT_WINDOW_KEY` initializer projected every figure onto `today` even
  // when the turn resolved to a window outside the closed 5-key enum, so the
  // tiles rendered "not available" over real data (arm.PNG).
  const turnWindow = useMemo(() => resolveTurnWindow(data), [data]);
  const [selection, setSelection] = useState<WindowSelection>(() =>
    initialWindowSelection(resolveTurnWindow(data))
  );

  if (!data || !Array.isArray(data.tabs) || data.tabs.length === 0) {
    return (
      <Empty
        message={translate("card.empty", { defaultValue: "No machine evidence in this card yet." }) as string}
      />
    );
  }

  const freshness = resolveFreshness(data.freshness);
  const exception = deriveExceptionBanner(data);
  const overview = isOverviewCard(data);
  const identityTitle = resolveIdentityTitle(data);
  // Show the pill row ONLY when the card carries windowed figures; a card whose
  // figures are ALL snapshots hides it entirely (not disabled).
  const hasWindows = cardHasWindowedFigures(data);

  // A5: the pill row, the hero suffix and the overview row tags must all name the
  // SAME window — resolved once here.
  const windowLabel = windowSelectionLabel(selection, turnWindow, translate);

  const activeTab = ordered.find((t) => t.part_slug === activeSlug) ?? ordered[0];
  // Provenance footer scoped to the ACTIVE tab's figures (join figures→sources),
  // projected onto the selected window so source_ref lines up with what renders.
  const footerEntries = buildProvenanceFooter({
    ...data,
    tabs: [
      {
        ...activeTab,
        figures: (activeTab.figures ?? []).map((f) => projectFigureToSelection(f, selection)),
      },
    ],
  });

  return (
    <div
      className="msc-machine-card"
      data-overview={overview ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 16,
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        fontFamily: dt.font,
        color: dt.textPrimary,
      }}
    >
      {/* Identity header — display_name only (the GUID rides the identity block
          in the model but is NEVER rendered; see the GUID guard in the engine).
          ME-CARDS-R1 A1: `resolveIdentityTitle` now SKIPS a GUID-bearing
          candidate and returns "", so the header shows the translated fallback
          rather than the raw GUID the live payload carried. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>
          {identityTitle ||
            (translate("card.titleFallback", { defaultValue: "Machine evidence" }) as string)}
        </span>
        {/* A5: the locked design's one-line card descriptor. An overview card
            says what the machine IS; a per-part card keeps the generic subtitle
            (its section descriptor renders inside the section body). */}
        <span style={{ fontSize: 11, color: dt.textMuted, fontFamily: dt.fontMono, letterSpacing: "0.04em" }}>
          {overview
            ? (translate("card.subtitleOverview", {
                defaultValue: "Part-picker / tray-loader cell · machine overview",
              }) as string)
            : (translate("card.subtitle", { defaultValue: "MASH machine evidence" }) as string)}
        </span>
      </div>

      <FreshnessBanner resolution={freshness} />

      {/* GAP-8: promoted exception banner ABOVE the hero (DARK feed OR non-zero
          safety/alarm figure). camera_inventory stays a card-level WATCH. */}
      {exception.promote ? <ExceptionBanner resolution={exception} /> : null}

      {hasWindows ? (
        <PeriodPillRow selection={selection} turnWindow={turnWindow} onSelect={setSelection} />
      ) : null}

      {overview ? (
        <OverviewRollup card={data} onSelect={setActiveSlug} windowLabel={windowLabel} />
      ) : null}

      {/* Tab strip — ordered by the fixed six-tab order; a single-part card shows
          just its one section header. Tab-list-agnostic: a stray slug still renders. */}
      {ordered.length > 1 ? (
        <div
          className="msc-tab-strip"
          role="tablist"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, borderBottom: `1px solid ${dt.rowBorder}`, paddingBottom: 8 }}
        >
          {ordered.map((t) => {
            const active = t.part_slug === activeTab.part_slug;
            return (
              <button
                key={t.part_slug}
                type="button"
                role="tab"
                aria-selected={active}
                data-tab-slug={t.part_slug}
                onClick={() => setActiveSlug(t.part_slug)}
                style={{
                  padding: "4px 12px",
                  borderRadius: dt.radiusPill,
                  fontFamily: dt.font,
                  fontSize: 12,
                  fontWeight: active ? 600 : 500,
                  color: active ? dt.textPrimary : dt.textSecondary,
                  background: active ? dt.blueSoft : "transparent",
                  border: `1px solid ${active ? dt.cardBorder : "transparent"}`,
                  cursor: "pointer",
                }}
              >
                {translateTabLabel(t)}
              </button>
            );
          })}
        </div>
      ) : (
        <span style={{ fontSize: 13, fontWeight: 600, color: dt.textSecondary }}>{translateTabLabel(activeTab)}</span>
      )}

      <SectionBody
        tab={activeTab}
        card={data}
        dim={freshness.dim}
        selection={selection}
        windowLabel={hasWindows ? windowLabel : undefined}
      />

      <ProvenanceFooter entries={footerEntries} asOf={data.freshness?.as_of} />
    </div>
  );
};

export default MidasArtifactMachineCardV3;
